import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RowsPerPageSelect } from '@/components/ui/RowsPerPageSelect';

interface ActivityPaginationProps {
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function ActivityPagination({
  totalCount,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: ActivityPaginationProps) {
  const fromIndex = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const toIndex = Math.min(page * pageSize, totalCount);

  return (
    <div className="border-t border-line px-5 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs bg-surface">
      <span className="text-ink-3 font-medium">
        Showing {fromIndex} to {toIndex} of {totalCount} activities
      </span>

      <div className="flex items-center gap-4">
        <RowsPerPageSelect value={pageSize} options={[5, 10, 20]} onChange={onPageSizeChange} />

        {/* Page number buttons */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface-2 text-ink hover:bg-surface-3 disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={cn(
                'grid h-7 w-7 place-items-center rounded-[6px] text-xs font-bold transition-colors cursor-pointer',
                p === page
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'border border-line bg-surface-2 text-ink hover:bg-surface-3',
              )}
            >
              {p}
            </button>
          ))}

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(Math.min(page + 1, totalPages))}
            className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface-2 text-ink hover:bg-surface-3 disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
